import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastLogisticianRights } from "@/lib/rights/use-viewer-has-right";
import { useLineItemRejectReasons } from "@/pages/(app)/returns/refund/hooks/use-line-item-reject-reason";
import TextAreaWithSuggestions from "@/pages/(app)/returns/refund/validate/text-area-with-suggestion";
import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Badge } from "@babackapp/ui/components/badge";
import NumberFlow from "@number-flow/react";
import { useMemo } from "react";
import { ArticleItem } from "../article-item/article-item";
import { useRejectLineItemsListSections } from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-list-section";
import useRejectLineItemsQuantitiesSummary from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-quantities-summary";
import { useRejectLineItem } from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-context";
import {
  useNotTreatedByLogistician,
  useSetNotTreatedByLogistician,
} from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-state-context";

export default function ArticlesNotTreatedByLogistician() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { itemsNotTreatedByLogistician } = useRejectLineItemsListSections();
  const hasPermissionToView = useViewerHasAtLeastLogisticianRights();
  const summary = useRejectLineItemsQuantitiesSummary({
    state: "not-treated-by-logistician",
  });

  /**
   * If the shop has activated the logistician workflow, we don't show this section,
   * because it's useless
   */
  if (!shop.workflowLogistician.activated) {
    return null;
  }

  if (summary.max <= 0 || !hasPermissionToView) {
    return null;
  }

  return (
    <AccordionItem value="notTreatedByLogistician">
      <AccordionTrigger>
        <span>
          {t("returnDetail.confirmModal.notTreatedByLogistician")}
          <Badge variant="secondary" className="ml-2">
            <NumberFlow value={summary.selected} suffix={` / ${summary.max}`} />
          </Badge>
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="border-border border rounded-md flex-col gap-4 divide-y overflow-hidden">
          {itemsNotTreatedByLogistician.map((lineItem) => (
            <ArticleNotTreatedBylogistician
              key={lineItem.line_item_id}
              lineItem={lineItem}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

const ArticleNotTreatedBylogistician = ({
  lineItem,
}: {
  lineItem: ReturnRequestLineItemDto;
}) => {
  const { item, updateResolution, updateVariant } = useRejectLineItem(lineItem);
  const quantityValue = useNotTreatedByLogistician(lineItem.line_item_id);
  const updateQuantity = useSetNotTreatedByLogistician(lineItem.line_item_id);

  /**
  When an article is not returned, it has no resolution.
  When the merchant adds an article to the request, we need to add a resolution to it.
  So if the quantity is 0, remove the resolution, otherwise add it, default to refund
  */
  const _updateQuantity = (quantity: number) => {
    if (quantity === 0) updateResolution(null);
    else if (!item.resolution) updateResolution("refund");
    updateQuantity(quantity);
  };

  const { addLineItemReason, lineItemsReason } = useLineItemRejectReasons();
  const editineItemReason = addLineItemReason(lineItem.line_item_id);

  const selectLineItemReason = useMemo(() => {
    return lineItemsReason[lineItem.line_item_id]?.reason;
  }, [lineItemsReason, lineItem.line_item_id]);

  return (
    <>
      <ArticleItem
        lineItem={item}
        quantity={quantityValue.selectedQuantity}
        maxQuantity={quantityValue.maxQuantity}
        onChangeQuantity={_updateQuantity}
        onChangeVariant={updateVariant}
        onChangeResolution={updateResolution}
        hideStatus
        hideResolution
      />
      {quantityValue.selectedQuantity > 0 && (
        <TextAreaWithSuggestions
          onChangeReason={editineItemReason}
          reason={selectLineItemReason}
        />
      )}
    </>
  );
};
